import { SearchMemberProps } from "../care-coordination/case/detail/components/SearchMember/SearchMember";
import { 
  getOwnerDisplayLabel, 
  getTaskOwnerByReference, 
  TaskSelectedMemberOption, 
  TaskSelectedMemberValue 
} from "src/careflow/care-coordination/task/patient/TasksGrid/TaskModal";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import useLocales from "src/careflow/ph-core/internationalization/hook/useLocales";
import { useCallback, useEffect } from "react";
import useObjectState from "src/hooks/useObjectState";
import { PractitionerRoleWrapper, WrappedPractitionerRole } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole";
import useAuth from "src/careflow/ph-core/authentication/hook/useAuth";
import { getReferenceId, getWrappedByReference } from "src/utils/fhir";
import { Bundle, PractitionerRole } from "src/careflow/ph-core/fhir-client/resources";
import { fhirClient } from "src/App";
import { ReferenceResources } from "../helpers/types";
import { isEmpty } from "lodash";
import Iconify from "src/components/Iconify";

export interface SearchMemberModalProps extends SearchMemberProps{
  valueSelected: any;
  options: any;
  labelText: string;
  updateSelectedMember: Function;
  updateState: Function;
  stateKey: string;
  selectedOption: string;
  isRequired?: boolean;
  error?: boolean;
  helperText?: string;
  addManually?: boolean;
}

const SelectSearchMember = ({
  patient,
  isLoading,
  valueSelected,
  options,
  labelText,
  updateSelectedMember,
  updateState,
  stateKey,
  selectedOption,
  isRequired,
  error,
  helperText,
  addManually
}: SearchMemberModalProps) => {
  const user = useAuth();
  const { i18n } = useLocales();

  const [{ practitionerRoles, valueSelectDisplay, isLoadingSelect }, updateStateSelect] =
    useObjectState<{
      isLoadingSelect: boolean;
      practitionerRoles: WrappedPractitionerRole[];
      valueSelectDisplay: any
    }>({
      practitionerRoles: [],
      valueSelectDisplay: null,
      isLoadingSelect: false
    });

  const getPractitionerRoles = useCallback(async () => {
    try {
      if (!Array.isArray(options) || !options.find((e) => e?.value === 'Me')) return;
      updateStateSelect({ isLoadingSelect: true });
      
      const selectedOrg = user.getSelectedOrg()?.fhir_uri.split('Organization/')?.[1];
      const userPractitionerId = getReferenceId(user?.getCurrentUser()?.user_fhir_uri);
      
      const practitionerRolesResources = await fhirClient.search<PractitionerRole>(
        `PractitionerRole?active=true&practitioner=${userPractitionerId}&organization=${selectedOrg}`
      );

      const practitionerRoles = practitionerRolesResources?.
        entry?.reduce?.<WrappedPractitionerRole[]>((resources, { resource }) => {
          if (!resource) return resources;
          return [...resources, PractitionerRoleWrapper(resource)];
      }, []) ?? [];

      updateStateSelect({ practitionerRoles: practitionerRoles });
      
    } catch (error) {}

    updateStateSelect({ isLoadingSelect: false });
  }, [user, options]);

  useEffect(() => {
    getPractitionerRoles();
  }, [getPractitionerRoles]);

  const getValueSelectedDisplay = useCallback(async () => {
    try {
      updateStateSelect({ isLoadingSelect: true });

      if(!valueSelected) { 
        updateStateSelect({ valueSelectDisplay: null }); 
        return
      };

      if(valueSelected?.isAddOption){
        updateStateSelect({ valueSelectDisplay: valueSelected });
      }

      if(valueSelected?.resourceType){
        const name = getOwnerDisplayLabel(valueSelected);
        if(!isEmpty(name) || valueSelected?.value){
          updateStateSelect({ valueSelectDisplay: valueSelected });
          return;
        };
      }

      if(valueSelected?.reference){
        const resource = getTaskOwnerByReference(valueSelected);
        const name = getOwnerDisplayLabel(resource);
        const getWrapped = await getWrappedByReference(
          {
            reference: `${resource?.resourceType}/${resource?.id}`,
            display: `${name}`
          }
        );

        updateStateSelect({ valueSelectDisplay: getWrapped });
      }
    } 
    catch (error) {}
    finally {
      updateStateSelect({ isLoadingSelect: false })
    }
  }, [valueSelected]);

  useEffect(() => {
    getValueSelectedDisplay();
  }, [getValueSelectedDisplay]);

  return (
    <Autocomplete
      fullWidth
      disabled={isLoading || isLoadingSelect}
      value={valueSelectDisplay}
      onChange={async (_: React.SyntheticEvent, value: any) => {
        const optionAsValue = value as TaskSelectedMemberValue;
        const optionAsOption = value as TaskSelectedMemberOption;
        if (
          !optionAsValue?.resourceType &&
          optionAsOption?.value !== 'Patient' &&
          optionAsOption?.value === 'Me'
        ) {
          updateSelectedMember({
            isSearchMemberOpen: practitionerRoles?.length === 1 ? false : true,
            [selectedOption]: practitionerRoles?.length === 1 ? 
              practitionerRoles?.[0] : optionAsOption,
            inputSelected: stateKey,
          });
          updateState({ [stateKey]: practitionerRoles?.length === 1 ? 
            practitionerRoles?.[0] : optionAsOption });
          return;
        }
        if (
          !optionAsValue?.resourceType &&
          optionAsOption?.value !== 'Patient' &&
          optionAsOption
        ) {
          if(value?.isAddOption){
            updateSelectedMember({
              isSearchMemberOpen: false,
              [selectedOption]: optionAsOption,
              inputSelected: stateKey,
            });
            updateState({ [stateKey]: optionAsOption });
            return;
          }

          updateSelectedMember({
            isSearchMemberOpen: true,
            [selectedOption]: optionAsOption,
            inputSelected: stateKey,
          });
          return;
        }
        if (
          optionAsValue?.resourceType &&
          optionAsOption?.value !== 'Patient' &&
          optionAsOption
        ) {
          updateSelectedMember({
            isSearchMemberOpen: false,
            [selectedOption]: optionAsOption,
            inputSelected: stateKey,
          });
          updateState({ [stateKey]: optionAsOption });
          return;
        }
        if (optionAsOption?.value === 'Patient') {
          updateSelectedMember({
            isSearchMemberOpen: false,
            [selectedOption]: patient,
            inputSelected: stateKey,
          });
          updateState({ [stateKey]: patient });
          return;
        }
        updateSelectedMember({ [selectedOption]: null, inputSelected: null });
        updateState({ [stateKey]: null });
      }}
      options={options ?? []}
      getOptionLabel={(option) => {
        const optionAsValue = option as TaskSelectedMemberValue;
        const optionAsOption = option as TaskSelectedMemberOption;

        if ((option as unknown as ReferenceResources)?.reference)
          return (option as unknown as ReferenceResources)?.display ?? '';

        if (!optionAsValue?.resourceType) return optionAsOption?.label as string;
        let display = getOwnerDisplayLabel(optionAsValue);
        if (optionAsValue?.resourceType === 'Patient') {
          display = `${display} - Current ${i18n('patients.title', 'crs')}`;
        }

        return display;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`${i18n(labelText, 'crs')}${isRequired ? "*" : ""}`}
          error={!!error}
          helperText={helperText}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && isLoadingSelect && !patient ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      {...(addManually ? {
        filterOptions: (options: any, state) => {
          const filteredOptions = options.filter((i) =>
            i.label?.toLowerCase().includes(state.inputValue.toLowerCase())
          );
        
          const input = state.inputValue?.trim();
          const alreadyExists = options.some(
            (opt) => opt.label.toLowerCase() === input.toLowerCase()
          );
        
          if (input && !alreadyExists) {
            return [
              ...filteredOptions,
              {
                label: input,
                value: input,
                isAddOption: true as any,
              }
            ];
          }
        
          return filteredOptions;
        },
        renderOption: (props, option: any) => {
          const optionAny = option as any;
        return (
          <li {...props}>
            {optionAny?.isAddOption ? (
              <>
                <Iconify icon="ic:baseline-add" style={{ marginRight: 8 }} />
                Add&nbsp;<strong>"{option.label}"</strong>
              </>
            ) : (
              option.label
            )}
          </li>
        )
      }
      } : {})}
    />
  )
};

export default SelectSearchMember;
